import * as React from "react";
import { useToasts } from "react-toast-notifications";
import { Dialog } from "../../bits/Dialog/Dialog";
import store from "../../../../store";
import {
    ClinicianOfficeWindowRequests,
    DateFormats,
    HeldOfficeProps,
    IClinician,
    IOfficeWindowSchedule,
    OfficeRequestStatus,
    SignatureData,
} from "intake_shared/src/types";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { getStringFromScheduleDate } from "../../../../utils/utils";
import { ApproveWithChangesDialog } from "../ApproveWithChangesDialog/ApproveWithChangesDialog";
import { RejectRequestDialog } from "../RejectRequestDialog/RejectRequestDialog";
import HoldWindow from "../HoldWindow/HoldWindow";
import { Select } from "../../bits/Select/Select";
import styles from "./CheckAvailabilityDialog.module.scss";
import { CacheContext } from "../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../utils/intakeEmailSignature";

export interface RequestToPartiallyApproveProps {
    clinician_name: string;
    start_date: Date;
    end_date: Date;
    location: string;
    office: string;
    id: string;
    requester: string;
}

export interface IDraftApproveWithChangesRequest {
    from: string;
    to: string;
    reason: string;
    office: string;
}

interface CheckAvailabilityDialogProps {
    showCheckAvailability: boolean;
    setShowCheckAvailability: (value: React.SetStateAction<boolean>) => void;
    row: ClinicianOfficeWindowRequests;
    conflict: IOfficeWindowSchedule;
    setRefreshKey: (value: React.SetStateAction<number>) => void;
    requestToApproveWithChanges: RequestToPartiallyApproveProps;
    setDraftApproveWithChanges: (
        value: React.SetStateAction<IDraftApproveWithChangesRequest>
    ) => void;
    draftApproveWithChanges: IDraftApproveWithChangesRequest;
    officeRequestToReject: any;
    loggedInUserEmail: string;
    clinicianToApproveData: IClinician;
    officeDetailsToHold: any;
}

export function CheckAvailabilityDialog({
    showCheckAvailability,
    setShowCheckAvailability,
    row,
    conflict,
    setRefreshKey,
    requestToApproveWithChanges,
    setDraftApproveWithChanges,
    draftApproveWithChanges,
    officeRequestToReject,
    loggedInUserEmail,
    clinicianToApproveData,
    officeDetailsToHold,
}: CheckAvailabilityDialogProps) {
    const { addToast } = useToasts();
    const olc = React.useContext(CacheContext);
    const intakeSignature = getIntakeEmailSignature(olc);
    const [approveWithChangesRequest, setApproveWithChangesRequest] =
        React.useState(false);
    const [rejectRequest, setRejectRequest] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [heldWindows, setHeldWindows] = React.useState([] as HeldOfficeProps[]);
    const [shouldHoldWindow, setShouldHoldWindow] = React.useState(false);
    const [action, setAction] = React.useState<
        "hold_window" | "hold_with_changes"
    >();

    const [conflictAction, setConflictAction] = React.useState<string>("");
    const [noConflictAction, setNoConflictAction] = React.useState<string>("");

    // Define the select options
    const conflictOptions = [
        { value: "approve_with_changes", label: "Approve with changes" },
        { value: "hold_with_changes", label: "Hold window with changes" },
        {
            value: "reject",
            label: <span className={styles.rejectRequest}>Reject Request</span>,
        },
    ];

    const noConflictOptions = [
        { value: "approve", label: "Approve Request" },
        { value: "approve_with_changes", label: "Approve with changes" },
        { value: "hold_window", label: "Hold window" },
        { value: "hold_with_changes", label: "Hold window with changes" },
        {
            value: "reject",
            label: <span className={styles.rejectRequest}>Reject Request</span>,
        },
    ];

    const handleApproveOfficeWindow = async (row: ClinicianOfficeWindowRequests) => {
        setLoading(true);

        const bookingData = {
            id: uuidv4(),
            clinicianId: row.requester,
            office: row.office,
            location: row.location,
            start: getStringFromScheduleDate(row.start_date),
            end: getStringFromScheduleDate(row.end_date),
            ending_date:
                row.ending_date && getStringFromScheduleDate(row.ending_date),
            status: OfficeRequestStatus.APPROVED,
            assigned_to: row.clinician_name as string,
            available: false,
            office_window_request_id: row.office_window_request_id as string,
            office_manager: loggedInUserEmail as string,
            category: row.category,
            intakeSignature: intakeSignature || ({} as SignatureData),
        };
        await store.approveClinicianOfficeWindow(bookingData);
        setRefreshKey((prevKey) => prevKey + 1);
        setLoading(false);

        setShowCheckAvailability(false);
        addToast("Office window saved", {
            appearance: "success",
        });
    };

    const handleClose = () => {
        setShowCheckAvailability(false);
    };

    const fetchHeldSpaces = async (location: string) => {
        const heldRequests = await store.getAllHeldOfficeWindowsByLocation(location);
        const filteredRequest = heldRequests.map((item) => {
            return {
                clinicianId: item.clinician_name,
                start: item.start_date,
                end: item.end_date,
                location: item.location,
                office: item.office,
            };
        });
        setHeldWindows(filteredRequest);
    };

    React.useEffect(() => {
        if (row) {
            fetchHeldSpaces(row?.location);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [row?.office_window_request_id]);

    const handleConflictActionChange = (option: any) => {
        setConflictAction(option?.value);

        switch (option?.value) {
            case "approve_with_changes":
                setShowCheckAvailability(false);
                setApproveWithChangesRequest(true);
                break;
            case "hold_with_changes":
                setShowCheckAvailability(false);
                setAction("hold_with_changes");
                setShouldHoldWindow(true);
                break;
            case "reject":
                setShowCheckAvailability(false);
                setRejectRequest(true);
                break;
        }
    };

    const handleNoConflictActionChange = (option: any) => {
        setNoConflictAction(option?.value);

        switch (option?.value) {
            case "approve":
                handleApproveOfficeWindow(row);
                break;
            case "approve_with_changes":
                setShowCheckAvailability(false);
                setApproveWithChangesRequest(true);
                break;
            case "hold_window":
                if (row?.status !== OfficeRequestStatus.HELD) {
                    setShowCheckAvailability(false);
                    setShouldHoldWindow(true);
                    setAction("hold_window");
                }
                break;
            case "hold_with_changes":
                if (row?.status !== OfficeRequestStatus.HELD) {
                    setShowCheckAvailability(false);
                    setAction("hold_with_changes");
                    setShouldHoldWindow(true);
                }
                break;
            case "reject":
                setShowCheckAvailability(false);
                setRejectRequest(true);
                break;
        }
    };

    return (
        <>
            <Dialog
                open={showCheckAvailability}
                onClose={handleClose}
                title={`${row?.clinician_name} - ${row?.location} (${
                    row?.office
                }) ${moment(row?.start_date).format(DateFormats.DAY)} - ${moment(
                    row?.start_date
                ).format(DateFormats.HOUR_MIN_FORMAT)} - ${moment(
                    row?.end_date
                ).format(DateFormats.HOUR_MIN_FORMAT)}`}
            >
                <div className="v2-form">
                    {conflict
                        ? `This slot has a conflicting schedule with ${
                              conflict.clinicianId
                                  ? conflict.clinicianId
                                  : "this clincians's remote schedule"
                          },${conflict.clinicianId ? `${conflict.location},` : ""} ${
                              conflict.office
                          } @ ${moment(
                              getStringFromScheduleDate(conflict.start)
                          ).format(DateFormats.DAY_HOUR_FORMAT)} - ${moment(
                              getStringFromScheduleDate(conflict.end)
                          ).format(
                              DateFormats.DAY_HOUR_FORMAT
                          )}, however you can approve this request with changes or reject it.`
                        : "This slot is currently available"}
                    {conflict ? (
                        <Select
                            label="Select Action"
                            options={conflictOptions}
                            value={conflictOptions.find(
                                (opt) => opt.value === conflictAction
                            )}
                            onChange={handleConflictActionChange}
                            placeholder="Choose an action..."
                            isRequired
                            isLongListInDialog
                        />
                    ) : (
                        <Select
                            label="Select Action"
                            options={noConflictOptions.filter((opt) => {
                                if (row?.status === OfficeRequestStatus.HELD) {
                                    return !opt.value.includes("hold");
                                }
                                return true;
                            })}
                            value={noConflictOptions.find(
                                (opt) => opt.value === noConflictAction
                            )}
                            onChange={handleNoConflictActionChange}
                            placeholder="Choose an action..."
                            isDisabled={loading}
                            isLongListInDialog
                        />
                    )}
                </div>
            </Dialog>

            <RejectRequestDialog
                rejectRequest={rejectRequest}
                officeRequestToReject={officeRequestToReject}
                setRejectRequest={setRejectRequest}
                setRefreshKey={setRefreshKey}
                requestToRejectId={officeRequestToReject.id}
                loggedInUserEmail={loggedInUserEmail as string}
                viewFrom="check_availability"
            />

            <ApproveWithChangesDialog
                approveWithChangesRequest={approveWithChangesRequest}
                setApproveWithChangesRequest={setApproveWithChangesRequest}
                requestToApproveWithChanges={requestToApproveWithChanges}
                row={row}
                setRefreshKey={setRefreshKey}
                setDraftApproveWithChanges={setDraftApproveWithChanges}
                draftApproveWithChanges={draftApproveWithChanges}
                clinicianToApproveData={clinicianToApproveData}
                heldWindows={heldWindows}
            />

            <HoldWindow
                shouldHoldWindow={shouldHoldWindow}
                setShouldHoldWindow={setShouldHoldWindow}
                detailsToHold={officeDetailsToHold}
                setRefreshKey={setRefreshKey}
                action={action}
                clinicianToApproveData={clinicianToApproveData}
                heldWindows={heldWindows}
            />
        </>
    );
}
